import { CRMOptionSetValue, CRMSimpleItem } from 'store/modules/options/crmTypes';

// Global type definitions for our custom objects
type Bank = {
  name: string;
  smartPayroll: boolean;
  id: string;
  isCalculatorOnly: boolean;
  isFullService: boolean;
  level2: boolean;
  processingFeeApplies: boolean;
  isBankOther: boolean;
}

export enum CallbackTypes {
  STANDARD = 'standard',
  SILENT = 'silent',
  CONTACT_SILENT = 'contact silent',
}

export type PayCycles = 'Monthly' | 'Fortnightly' | 'Weekly';

export interface IPayRun {
  employeeCount?: number;
  payCycle?: PayCycles;
}

// @TODO rename to workItOutData
export interface IWorkItOutData {
  payRuns: IPayRun[];
  managePayCycles?: PayCycleTypes;
  manageHr?: string;
  bankChoice?: Bank;
  bankOther?: string;
}

export interface IPlan {
  id: string;
  title: string;
  text: string;
  features: string[];
  isRecommended?: boolean;
  smartPayroll: boolean;
  isDisabled?: boolean;
  unavailableText?: string;
  crmId: string;
}

export interface IExtra {
  id: string;
  title: string;
  text: string;
  features: string[];
  isRecommended?: boolean;
}

export interface IPickAPlanData  {
  chosenPlan: string;
  extras: string[];
}

export interface ICity {
  id: string;
  name: string;
}

export interface IFlowOptions {
  bankOptions: Bank[];
  manageHrOptions: CRMOptionSetValue[];
  managePayCycleOptions: PayCycleOptions[];
  plans: IPlan[];
  extras: IExtra[];
  jobTitles: CRMOptionSetValue[];
  industryTypes: CRMSimpleItem[];

  payrollProcessingModes: CRMOptionSetValue[];
  competingProducts: CRMSimpleItem[];

  irdPaymentFrequencies: CRMOptionSetValue[];
  employeeLeaveRollover: CRMOptionSetValue[];
  
  leadSource: CRMSimpleItem[];
  accountantHasAccessTo: CRMOptionSetValue[];
  payCycles: CRMOptionSetValue[];

  cities: ICity[];
  regions: ICity[];

  leadChannels: CRMSimpleItem[];
}

export interface PromoCodeDiscount {
  title: string;
  code: string;
  description: string;
  startDate: string;
  endDate: string;
  monthlyDiscount?: number;
  baseSetupFeeDiscount?: number;
  crmId: string;
  referralCode: string;
}

export type PayCycleTypes = 'calc-only' | 'full-service';

export interface PayCycleOptions {
  id: PayCycleTypes;
  title: string;
  label: string;
  crmId: number;
}

export interface IContact {
  firstName: string;
  lastName: string;
  jobTitle: string;
  jobOther?: string;
  email: string;
  contactNumber: string;
}

export interface IContactDetails {
  contact?: IContact;
  additionalContact?: IContact;
  isAccountant?: boolean;
  isAdditionalPrimaryContact?: boolean;
}

export interface ICompanyDetails {
  companyName?: string;
  tradingName?: string;
  irdNumber?: string;
  industryType?: string;
  businessAddress?: IAddress;
  postalAddress?: IAddress;
  postalSameAsBusiness?: boolean;

  charityRegistrationNumber?: string;

  accountant?: AccountantTuple; // Option from SmartPayroll
  
  hasSkippedAccountantDetails?: boolean;
  accountantNotListed?: boolean;
  accountantBusinessName?: string; // Only appears if customer selects accountantNotListed
  accountantPhoneNumber?: string; // Only appears if customer selects accountantNotListed
  
  accountantHasAccessTo?: string; // Pick list
  // Read only payroll
  // Reporting payroll
  // Processing payroll
  // Authorise payroll
  // Master User/All
  // No payroll access

  accountantName?: string; // Only presented if gives some permission - i.e. any selection other than 'No payroll access'
  accountantWillPaySmartPayrollFees?: boolean; // Only show if customer has checked they are an accountant completing on behalf of the customer on SIG01-03

  // Only show if customer has checked the box that states they want the customer added to their umbrella account 
  shouldAddToAccountantsUmbrellaPage?: boolean;
  umbrellaPageName?: string; // Required if shouldAddToAccountantsUmbrellaPage === true
}

export interface IAddress {
  address?: AddressPrediction; // This is the RAW UI address from places lookup
  isManual?: boolean;
  // These are the values from the places detailed lookup OR manual entry
  addressLineOne?: string;
  addressLineTwo?: string;
  addressLineThree?: string;
  city?: ICity;
  postCode?: string;
  region?: ICity;
  country?: string;
}
export interface ISubmitData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  comment?: string;
  isAccountant?: boolean;

  // @TODO Might add this field to the callback modal, at the moment,
  // in the callback-request/sagas, we get companyName from the store if it's present
  companyName?: string;
}

export interface ISilentSubmitData extends Partial<ISubmitData> {
  accountantName?: string;
  accountantBusinessName?: string;
  accountantPhoneNumber?: string;
}

export interface ISubmitSuccessData {
  success: boolean;
}

export interface ISubmitFailData {
  error: string;
}

export interface AddressPrediction {
  description: string;
  id: string;
  place_id: string;
  reference: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  }
}

export interface AddressDetails {
  "address_components": Array<{
    "long_name": string; // "6011",
    "short_name": string; // "6011",
    "types": string[]; //  "postal_code"
  }>;
  "adr_address": string; // "<span class=\"street-address\">7 Dixon Street</span>, <span class=\"extended-address\">Te Aro</span>, <span class=\"locality\">Wellington</span> <span class=\"postal-code\">6011</span>, <span class=\"country-name\">New Zealand</span>",
  "formatted_address": string; // "7 Dixon Street, Te Aro, Wellington 6011, New Zealand",
  "geometry": {
    "location": {
      "lat": number; // -41.292416,
      "lng": number; // 174.7783363
    };
    "viewport": {
      "northeast": {
        "lat": number; // -41.29102486970849,
        "lng": number; // 174.7797119802915
      };
      "southwest": {
        "lat": number; // -41.29372283029149,
        "lng": number; // 174.7770140197085
      };
    };
  };
  "icon": string; // "https://maps.gstatic.com/mapfiles/place_api/icons/geocode-71.png",
  "name": string; // "7 Dixon Street",
  "place_id": string; // "ChIJm7SBTdevOG0R-_R_eK-Op5s",
  "plus_code": {
    "compound_code": string; // "PQ5H+28 Wellington, New Zealand",
    "global_code": string; // "4VCPPQ5H+28"
  };
  "reference": string; // "ChIJm7SBTdevOG0R-_R_eK-Op5s",
  "types": string[]; // "street_address"
  "url": string; // "https://maps.google.com/?q=7+Dixon+Street,+Te+Aro,+Wellington+6011,+New+Zealand&ftid=0x6d38afd74d81b49b:0x9ba78eaf787ff4fb",
  "utc_offset": number; // 720,
  "vicinity": string; // "Te Aro"
}

export interface IPayroll {
  bankAccountName: string;
  bankAccountNumber: string;
  authoriseDirectDebitMonthlyFees: boolean;
  notAuthorisedButRequestDirectDebitAuthority: boolean;
  processingPayroll: string;
  processingPayrollOther?: string; // When processingPayroll is  "Other"
  otherProduct?: string; // When processingPayroll is "Other Payroll Software" - this doesn't get sen
  payePaymentsToIrd: string;
  employeeAnnualLeaveRollover: string;
  averageGrossAmountPerPayRun: number;
  payrollProcessingStart: string;
}

export interface ISummary {
  additionalComments: string;
  howDidYouHearAboutUs: string;
  howDidYouHearAboutUsOther: string;
  sendMeInformation: boolean;
  referralCode: string; 
}

export interface ISubmitPayload {
  workItOut: IWorkItOutData;
  pickAPlan: IPickAPlanData;

  isCharity: boolean;
  promoCode?: PromoCodeDiscount;

  contactDetails: IContactDetails;
  companyDetails: ICompanyDetails;
  payrollInformation: IPayroll;
  summary: ISummary;
}

// https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-oauth2-auth-code-flow#successful-response-1
export interface CRMBearerTokenData {
  access_token: string;
  expires_in: string; // "3599"
  expires_on: string; // "1597811863"
  ext_expires_in: string; // "3599"
  not_before: string; // "1597807963"
  resource: string; // "api://smartpayroll-devphase2.onmicrosoft.com/D365Api"
  token_type: string; // "Bearer"
}

export interface ITokenData {
  accessToken: string;
  expiresIn: string; // "3599"
  expiresOn: string; // "1597811863"
  extExpiresIn: string; // "3599"
  notBefore: string; // "1597807963"
  resource: string; // "api://smartpayroll-devphase2.onmicrosoft.com/D365Api"
  tokenType: string; // "Bearer"
}

export interface AccountantTuple {
  name: string;
  id: string;
}